import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { DateRange } from '@domain/value-objects/DateRange';

/**
 * A quién nombra la tarjeta de sugerencia de Inicio.
 *
 * La tarjeta existe para que alguien mueva sus fechas antes de elegirlas, así que
 * sólo puede hablar de ausencias que **todavía se pueden esquivar**. Tomaba la
 * primera de la semana por fecha de inicio, terminada o no, y afirmaba «ya está
 * fuera» en presente: el viernes seguía diciendo que alguien está fuera por unos
 * días que acabaron el lunes, y aconsejaba rodear un hueco que ya no existe.
 * Exacta sobre el pasado e inútil sobre lo que se decide.
 *
 * Vive aparte de la pantalla porque sus bordes se prueban mejor que se miran: una
 * ausencia que termina hoy todavía cuenta, una que terminó ayer no, y entre varias
 * vigentes gana la que está ocurriendo.
 */

/** Lo poco que la sugerencia necesita saber de una ausencia. */
export interface TipCandidate {
  readonly personName: string;
  readonly subAreaName: string;
  readonly range: DateRange;
}

export interface ChosenTip<T extends TipCandidate> {
  readonly absence: T;
  /** Si hoy cae dentro del rango. Decide el tiempo verbal de la frase. */
  readonly inProgress: boolean;
}

/**
 * La ausencia sobre la que vale la pena avisar, o `null` si no hay ninguna.
 *
 * **Termina hoy todavía cuenta.** `end.isBefore(today)` y no `!end.isAfter(today)`:
 * quien regresa mañana sigue fuera hoy, y descartarlo dejaría a la sub área corta
 * sin que nadie lo dijera justo el día que importa.
 *
 * Entre las vigentes gana la que está ocurriendo, y si ninguna lo está, la más
 * próxima a empezar — que es el orden en que alguien las va a encontrar al elegir
 * fechas.
 */
export const chooseAbsenceTip = <T extends TipCandidate>(
  absences: readonly T[],
  today: CalendarDate,
): ChosenTip<T> | null => {
  const live = [...absences]
    .filter((absence) => !absence.range.end.isBefore(today))
    .sort((a, b) => a.range.start.epochDay - b.range.start.epochDay);

  const chosen = live.find((absence) => absence.range.contains(today)) ?? live[0];
  if (chosen === undefined) return null;

  return { absence: chosen, inProgress: chosen.range.contains(today) };
};
